import { ChangeDetectionStrategy, Component, Input, inject, signal, computed, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CloudApiService, Package, PackageDetails } from '@/cloud/services/cloud-api.service';
import moment from 'moment';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';

interface PackageWithDetails {
    package: Package;
    latestVersion?: {
        assemblyVersion: string;
        buildTimeUtc?: string;
        uploadedUtc: string;
        isDebugBuild?: boolean;
        uploadSource?: string;
    };
}

@Component({
    selector: 'app-namespace-packages',
    standalone: true,
    imports: [CommonModule, RouterModule, TagModule, TooltipModule, EmptyStateComponent, LoadingSkeletonComponent],
    templateUrl: './namespace-packages.component.html',
    styleUrl: './namespace-packages.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NamespacePackagesComponent implements OnDestroy {
    private api = inject(CloudApiService);
    private router = inject(Router);

    /** Namespace ID to load packages for */
    @Input() set namespaceId(value: string | null) {
        this._namespaceId.set(value);
    }

    /** Max packages to display */
    @Input() maxItems = 5;

    private _namespaceId = signal<string | null>(null);

    packages = signal<PackageWithDetails[]>([]);
    loading = signal(false);
    error = signal<string | null>(null);

    private loadEffect = effect(() => {
        const nsId = this._namespaceId();
        if (nsId) {
            this.loadPackages(nsId);
        } else {
            this.packages.set([]);
        }
    });

    /** Limited packages for display */
    displayedPackages = computed(() => {
        return this.packages().slice(0, this.maxItems);
    });

    /** Total count */
    totalCount = computed(() => this.packages().length);

    /** Whether there are more packages */
    hasMore = computed(() => this.packages().length > this.maxItems);

    /** Hidden count */
    hiddenCount = computed(() => Math.max(0, this.packages().length - this.maxItems));

    deployPackage(pkg: PackageWithDetails): void {
        if (!pkg.latestVersion) return;
        const nsId = this._namespaceId();
        const queryParams: Record<string, string> = { deploy: pkg.package.id };
        if (nsId) queryParams['ns'] = nsId;
        this.router.navigate(['/packages'], { queryParams });
    }

    ngOnDestroy(): void {
        // Effect cleanup handled automatically
    }

    private async loadPackages(namespaceId: string): Promise<void> {
        this.loading.set(true);
        this.error.set(null);

        try {
            // First get list of packages
            const packageList = await this.api.getPackages(namespaceId);

            // Load details for each package to get latest version info
            const packagesWithDetails: PackageWithDetails[] = [];

            for (const pkg of packageList) {
                try {
                    const details = await this.api.getPackageDetails(pkg.id, 1);
                    packagesWithDetails.push({
                        package: details.package,
                        latestVersion: details.latestVersion
                            ? {
                                  assemblyVersion: details.latestVersion.assemblyVersion,
                                  buildTimeUtc: details.latestVersion.buildTimeUtc,
                                  uploadedUtc: details.latestVersion.uploadedUtc,
                                  isDebugBuild: details.latestVersion.isDebugBuild,
                                  uploadSource: details.latestVersion.uploadSource
                              }
                            : undefined
                    });
                } catch {
                    // If details fail, just add basic package info
                    packagesWithDetails.push({ package: pkg });
                }
            }

            // Sort by most recently updated
            packagesWithDetails.sort((a, b) => {
                const dateA = a.latestVersion?.uploadedUtc || a.package.updatedUtc;
                const dateB = b.latestVersion?.uploadedUtc || b.package.updatedUtc;
                return dateB.localeCompare(dateA);
            });

            this.packages.set(packagesWithDetails);
            this.loading.set(false);
        } catch (err: any) {
            console.error('Failed to load packages', err);
            this.error.set(err?.error || 'Failed to load packages');
            this.loading.set(false);
        }
    }

    formatDate(date: string | undefined): string {
        if (!date) return '—';
        const m = moment.utc(date);
        if (!m.isValid()) return '—';
        return m.local().format('MMM D, YYYY');
    }

    formatDateTime(date: string | undefined): string {
        if (!date) return '—';
        const m = moment.utc(date);
        if (!m.isValid()) return '—';
        return m.local().format('MMM D, YYYY HH:mm');
    }

    formatRelativeTime(date: string | undefined): string {
        if (!date) return '—';
        const m = moment.utc(date);
        if (!m.isValid()) return '—';
        return m.fromNow();
    }
}
